import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { QueryStatus } from "../../app/types";
import {
  AssessmentEvent,
  CccReportType,
  PaginatableAssessmentHistoryArgs,
  ProgramReportRow,
  ProgramRole,
  RequestBulkCCCReportInput,
  UserReportRow
} from "../../API";
import { Storage } from "aws-amplify";
import { parse } from "papaparse";
import { fetchActivityEventsByCreatorId } from "../ActivityEvents/activityEventsAPI";
import { fetchProgramMembershipsByProgramId } from "../ProgramMemberships/programMembershipsAPI";
import { fetchAssessmentEventsByRaterUserId } from "../AssessmentEvents/assessmentEventsAPI";
import { fetchProgramsByIntitutionId } from "../Programs/programsAPI";
import {
  fetchMilestoneReport,
  fetchPaginatedAssessmentHistory,
  fetchProgramReport,
  fetchUserReport,
  initiateBulkCCCReportGeneration
} from "./reportsAPI";

export type UserReport = {
  createdAssessmentEvents: number;
  assignedAssessmentEvents: number;
  completedAssessmentEvents: number;
  percentCompleted: number;
  pendingAssessmentEvents?: number;
  percentPending?: number;
  expiredAssessmentEvents: number;
  percentExpired: number;
  programMembershipId: string;
  programRole?: ProgramRole;
  programRoleString?: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  id?: string;
  program?: string | null;
  programId?: string | null;
};

let reportExtension = "";
switch (process.env.REACT_APP_ENV) {
  case "staging":
    reportExtension = "stg";
    break;
  case "production":
  case "prod":
    reportExtension = "prod";
    break;
  case "csdev":
    reportExtension = "prod";
    break;
}

export const roundToTwoDecimals = (numToRound: number) => {
  return Math.round((numToRound + Number.EPSILON) * 100) / 100;
};

export type ProgramReport = {
  id: string;
  institutionId: string;
  institutionName: string;
  programId: string;
  programName: string;
  createdAssessmentEvents: number;
  assignedAssessmentEvents: number;
  completedAssessmentEvents: number;
  percentCompleted: number;
  attendingsInProgram: number;
  traineesInProgram: number;
  averageCompletedPerTrainee: number;
};

const convertBlobToString = (blob: Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const text = reader.result;
      resolve(text as string);
    };

    reader.onerror = () => {
      reject(new Error("error reading blob as string"));
    };

    reader.readAsText(blob);
  });
};

type UserReportItem = {
  firstname?: string;
  lastname?: string;
  email?: string;
  n_assigned?: string;
  n_complete?: string;
  n_created?: string;
  n_expired?: string;
  name?: string;
  pct_complete?: string;
  pct_expired?: string;
  program_id?: string;
  programrole?: string;
  program_membership_id?: string;
  user_id?: string;
};

type ProgramReportItem = {
  assessments_per_trainee?: string;
  institution_id?: string;
  institution_name?: string;
  n_assigned?: string;
  n_attending?: string;
  n_complete?: string;
  n_created?: string;
  n_trainee?: string;
  program_id?: string;
  program_name?: string;
  pct_complete?: string;
};

async function generateBackendReport<ReportItemType>(
  fileName: string,
  setReport: Dispatch<SetStateAction<ReportItemType[]>>,
  setGeneratedDate: Dispatch<SetStateAction<Date | undefined>>,
  setStatus: Dispatch<SetStateAction<QueryStatus>>,
  setError: Dispatch<SetStateAction<string | null>>
) {
  try {
    setStatus("loading");
    const file = await Storage.get(fileName, { download: true });
    if (file.Body) {
      const fileString = await convertBlobToString(file.Body as Blob);
      const lastModified = file.LastModified;
      setGeneratedDate(lastModified);

      const parsed = parse<ReportItemType>(fileString, { header: true }).data;
      setReport(parsed);
    }
    setStatus("succeeded");
  } catch (error) {
    console.error("error fetching backend report:", error);
    setStatus("failed");
    setError(`error loading backend report: ${JSON.stringify(error)}`);
  }
}

export const useBackendUserReport = () => {
  const [report, setReport] = useState<UserReportItem[]>([]);
  const [generatedDate, setGeneratedDate] = useState<Date>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (reportExtension) {
      generateBackendReport<UserReportItem>(
        `user_report-${reportExtension}.csv`,
        setReport,
        setGeneratedDate,
        setStatus,
        setError
      );
    } else {
      console.error("no environment extension available to fetch report.");
    }
  }, []);

  return {
    report,
    status,
    error,
    generatedDate
  };
};

export const useDynamicUserReport = (
  programId?: string,
  startDate?: string,
  endDate?: string,
  role?: string
) => {
  const [report, setReport] = useState<UserReportRow[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getDynamicUserReport = async (
    programId: string,
    startDate: string,
    endDate: string,
    role?: string
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchUserReport(
        programId,
        startDate,
        endDate,
        role
      );
      if (results?.success) {
        setStatus("succeeded");
        setError(null);
        setReport(results?.userReportRows ?? []);
      } else {
        setStatus("failed");
        setError(results?.error ?? "Unknown Error");
        setReport([]);
      }
      // Dev note: need to be able to handle an error response from the API
    } catch (error) {
      console.error("error fetching dynamic user report:", error);
      setStatus("failed");
      setError(`error fetching dynamic user report: ${error}`);
    }
  };

  useEffect(() => {
    if (programId && startDate && endDate) {
      getDynamicUserReport(
        programId,
        startDate,
        endDate,
        role == "" ? undefined : role
      );
    }
  }, [programId, startDate, endDate, role]);

  return {
    report,
    status,
    error
  };
};

export const useDynamicProgramReport = (
  institutionId?: string,
  startDate?: string,
  endDate?: string
) => {
  const [report, setReport] = useState<ProgramReportRow[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getDynamicProgramReport = async (
    programId: string,
    startDate: string,
    endDate: string
  ) => {
    setStatus("loading");

    try {
      const results = await fetchProgramReport(programId, startDate, endDate);
      if (results?.success) {
        setStatus("succeeded");
        setError(null);
        setReport(results?.programReportRows ?? []);
      } else {
        setStatus("failed");
        setError(results?.error ?? "Unknown Error");
        setReport([]);
      }
    } catch (error) {
      console.error("error fetching dynamic program report:", error);
      setStatus("failed");
      setError(`error fetching dynamic program report: ${error}`);
    }
  };

  useEffect(() => {
    if (institutionId && startDate && endDate) {
      getDynamicProgramReport(institutionId, startDate, endDate);
    }
  }, [institutionId, startDate, endDate]);

  return {
    report,
    status,
    error
  };
};

const generateUserLevelReport = async (programId: string) => {
  const newReport: UserReport[] = [];
  const membershipsInProgram = await fetchProgramMembershipsByProgramId(
    programId
  );
  if (membershipsInProgram) {
    for (const membership of membershipsInProgram) {
      const programMember = membership.programMember;
      if (programMember) {
        const memberReport: UserReport = {
          ...programMember,
          createdAssessmentEvents: 0,
          assignedAssessmentEvents: 0,
          completedAssessmentEvents: 0,
          percentCompleted: 0,
          pendingAssessmentEvents: 0,
          percentPending: 0,
          expiredAssessmentEvents: 0,
          percentExpired: 0,
          programRole: membership.programRole,
          programMembershipId: membership.id,
          userId: membership.programMemberId
        };
        const activityEventsCreatedByMember =
          await fetchActivityEventsByCreatorId(membership.programMemberId);
        const activityEventsCreatedByMemberForProgram =
          activityEventsCreatedByMember?.filter(
            (activityEvent) => activityEvent.programId === programId
          );
        memberReport.createdAssessmentEvents =
          activityEventsCreatedByMemberForProgram?.length || 0;

        const assessmentEventsAssignedToUser =
          await fetchAssessmentEventsByRaterUserId(membership.programMemberId);

        memberReport.assignedAssessmentEvents =
          assessmentEventsAssignedToUser?.length || 0;

        // Should count as completed if there's a completion date and:
        // - there's an expiration date that is greater than the completion date, or
        // - there's no expiration date
        const usersCompletedAssessmentEvents =
          assessmentEventsAssignedToUser?.filter(
            (assessmentEvent) =>
              assessmentEvent.completionDate &&
              ((assessmentEvent.expirationDate &&
                new Date(assessmentEvent.completionDate) <
                  new Date(assessmentEvent.expirationDate)) ||
                !assessmentEvent.expirationDate)
          );
        memberReport.completedAssessmentEvents =
          usersCompletedAssessmentEvents?.length || 0;
        const percentCompleted =
          (memberReport.completedAssessmentEvents /
            memberReport.assignedAssessmentEvents) *
          100;
        if (percentCompleted) {
          memberReport.percentCompleted = roundToTwoDecimals(percentCompleted);
        }

        // Should count as pending if there's:
        // - no completion date AND
        // - no expiration date OR the expiration date is greater than right now
        const usersPendingAssessmentEvents =
          assessmentEventsAssignedToUser?.filter(
            (assessmentEvent) =>
              !assessmentEvent.completionDate &&
              (!assessmentEvent.expirationDate ||
                new Date(assessmentEvent.expirationDate) > new Date())
          );
        memberReport.pendingAssessmentEvents =
          usersPendingAssessmentEvents?.length || 0;
        const percentPending =
          (memberReport.pendingAssessmentEvents /
            memberReport.assignedAssessmentEvents) *
          100;
        if (percentPending) {
          memberReport.percentPending = roundToTwoDecimals(percentPending);
        }

        // Should count as expired if:
        // - there's an expiration date AND
        // - there's no completion date OR the completion date is greater than the expiration date
        const usersExpiredAssessmentEvents =
          assessmentEventsAssignedToUser?.filter((assessmentEvent) => {
            // jump out if there is no expiration date set
            if (!assessmentEvent.expirationDate) return false;

            const now = new Date();
            const expirationDate = new Date(assessmentEvent.expirationDate);
            const completionDate = assessmentEvent.completionDate
              ? new Date(assessmentEvent.completionDate)
              : null;

            const expirationDatePassed = expirationDate < now;
            const completedAfterExpiration =
              completionDate && completionDate > expirationDate;
            if (
              expirationDatePassed &&
              (!completionDate || completedAfterExpiration)
            ) {
              return true;
            } else {
              return false;
            }
          });
        memberReport.expiredAssessmentEvents =
          usersExpiredAssessmentEvents?.length || 0;
        const percentExpired =
          (memberReport.expiredAssessmentEvents /
            memberReport.assignedAssessmentEvents) *
          100;
        if (percentExpired) {
          memberReport.percentExpired = roundToTwoDecimals(percentExpired);
        }

        newReport.push(memberReport);
      }
    }
  }
  return newReport;
};

export const useUserReport = () => {
  const [report, setReport] = useState<UserReport[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const generateUserReport = async (programId: string) => {
    setStatus("loading");
    setError(null);
    try {
      const newReport = await generateUserLevelReport(programId);
      setReport(newReport);
      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
      console.error("error in generateUserReport:", error);
      setError(`error in generateUserReport ${error}`);
    }
  };

  return {
    report,
    status,
    error,
    generateUserReport
  };
};

export const useBackendProgramReport = () => {
  const [report, setReport] = useState<ProgramReportItem[]>([]);
  const [generatedDate, setGeneratedDate] = useState<Date>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (reportExtension) {
      generateBackendReport<ProgramReportItem>(
        `program_report-${reportExtension}.csv`,
        setReport,
        setGeneratedDate,
        setStatus,
        setError
      );
    } else {
      console.error("no environment extension available to fetch report.");
    }
  }, []);

  return {
    report,
    status,
    error,
    generatedDate
  };
};

export const useProgramReport = () => {
  const [report, setReport] = useState<ProgramReport[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const generateProgramReport = async (institutionId: string) => {
    setStatus("loading");
    setError(null);
    const newReport: ProgramReport[] = [];

    try {
      const programsInInstitution = await fetchProgramsByIntitutionId(
        institutionId
      );
      if (programsInInstitution) {
        for (const program of programsInInstitution) {
          const programReport: ProgramReport = {
            ...program,
            programId: program.id,
            programName: program.name,
            institutionName: program.institution?.name || "N/A",
            createdAssessmentEvents: 0,
            assignedAssessmentEvents: 0,
            completedAssessmentEvents: 0,
            percentCompleted: 0,
            attendingsInProgram: 0,
            traineesInProgram: 0,
            averageCompletedPerTrainee: 0
          };
          const userReportForProgram = await generateUserLevelReport(
            program.id
          );
          const createdAssessmentEventsInProgram = userReportForProgram.reduce(
            (total, { createdAssessmentEvents }) =>
              total + createdAssessmentEvents,
            0
          );
          const assignedAssessmentEventsInProgram = userReportForProgram.reduce(
            (total, { assignedAssessmentEvents }) =>
              total + assignedAssessmentEvents,
            0
          );
          const completedAssessmentEventsInProgram =
            userReportForProgram.reduce(
              (total, { completedAssessmentEvents }) =>
                total + completedAssessmentEvents,
              0
            );
          const traineeReports = userReportForProgram.filter(
            (userReport) => userReport.programRole === ProgramRole.Trainee
          );
          const assessmentEventsCompletedByTrainee = traineeReports.reduce(
            (total, { completedAssessmentEvents }) =>
              total + completedAssessmentEvents,
            0
          );
          const percentCompleted = roundToTwoDecimals(
            (completedAssessmentEventsInProgram /
              assignedAssessmentEventsInProgram) *
              100
          );
          const attendingsInProgram = userReportForProgram.filter(
            (userReport) => userReport.programRole === ProgramRole.Attending
          ).length;
          const traineesInProgram = userReportForProgram.filter(
            (userReport) => userReport.programRole === ProgramRole.Trainee
          ).length;
          const averageCompletedPerTrainee = roundToTwoDecimals(
            assessmentEventsCompletedByTrainee / traineesInProgram
          );
          programReport.createdAssessmentEvents =
            createdAssessmentEventsInProgram;
          programReport.assignedAssessmentEvents =
            assignedAssessmentEventsInProgram;
          programReport.completedAssessmentEvents =
            completedAssessmentEventsInProgram;
          if (percentCompleted) {
            programReport.percentCompleted = percentCompleted;
          }
          programReport.attendingsInProgram = attendingsInProgram;
          programReport.traineesInProgram = traineesInProgram;
          if (averageCompletedPerTrainee) {
            programReport.averageCompletedPerTrainee =
              averageCompletedPerTrainee;
          }
          newReport.push(programReport);
        }
      }
      setReport(newReport);
      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
      console.error("error in generateProgramReport:", error);
      setError(`error in generateProgramReport ${error}`);
    }
  };

  return {
    report,
    status,
    error,
    generateProgramReport
  };
};

export const useAssessmentHistory = (
  args?: PaginatableAssessmentHistoryArgs
) => {
  const [assessmentEvents, setAssessmentEvents] = useState<AssessmentEvent[]>(
    []
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAssessmentHistory = async (
    args: PaginatableAssessmentHistoryArgs
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchPaginatedAssessmentHistory(args);
      if (results) {
        setAssessmentEvents(results);
      }
      setStatus("succeeded");
    } catch (error) {
      console.error("error getting AssessmentHistory:", error);
      setStatus("failed");
      setError(JSON.stringify(error));
    }
  };

  useEffect(() => {
    if (args) {
      getAssessmentHistory(args);
    }
  }, [args]);

  return {
    assessmentEvents,
    status,
    error,
    getAssessmentHistory
  };
};

export const useMilestoneReport = (programMembershipIds?: string[]) => {
  const [milestoneReport, setMilestoneReport] = useState<string>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getMilestoneReport = async (programMembershipIds: string[]) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchMilestoneReport(programMembershipIds);
      if (results) {
        setMilestoneReport(results);
      }
      setStatus("succeeded");
    } catch (error) {
      const errorLabel = "error getting milestone report:";
      console.error(errorLabel, error);
      setStatus("failed");
      const msg =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null
            ? JSON.stringify(error)
            : String(error);
      setError(`${milestoneFetchError} ${msg}`);
    }
  };

  useEffect(() => {
    if (programMembershipIds) {
      getMilestoneReport(programMembershipIds);
    }
  }, [(programMembershipIds ?? []).join(",")]);

  return {
    milestoneReport,
    status,
    error
  };
};

// Errors
const milestoneFetchError = "There was an error fetching the milestone report.";

export const useGenerateBulkCCCReports = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const generateReports = async ({
    destinationEmail,
    reportTypes,
    userIds,
    includeSelfAssessments,
    startDate,
    endDate
  }: RequestBulkCCCReportInput) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await initiateBulkCCCReportGeneration({
        destinationEmail,
        reportTypes,
        userIds,
        includeSelfAssessments,
        startDate,
        endDate
      });
      console.log("result", result);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      console.error(error);
      setStatus("failed");
      setError(errorString);
    }
  };

  return {
    status,
    error,
    generateReports
  };
};
